import {getStore} from '@/services/store';
import {getCollection} from '@/lib/filters/filters';
import {isHyperLink} from '@/lib/ReusableFunctions/Wrappers';
import {currentUser} from '@/services/FirestoreService';
import {Markers} from '@/services/MapService';
import {InfoWindowContent} from '../InfoWindowContent';
import {Link} from '@/lib/classes/Link';
import {mappedFields, standardFields} from '@/lib/StandardFields';
import {DisplayField} from '../DisplayField';
import {featureOptions} from '@/services/OverlaysService/index.js';
import {NEW_LABEL_ID} from '@/lib/symbols';
import {watchEffect} from 'vue';

/**
 * @typedef {import('@/services/OverlaysService').Feature} Feature
 * @typedef {import('@/lib/classes/InfoWindowContent/InfoWindowContent').Toolbar} Toolbar
 * @typedef {import('@/lib/classes/InfoWindowContent/InfoWindowContent').InfoWindowContentData} InfoWindowContentData
 */

/**
 * We currently have many parcels saved to the database with displayFields in a different
 * format than ones we use while creating aggregations. To remedy this, this helper function converts
 * the old displayFields to the new format. The two places we need to do this on parcel displayFields
 * are for address and mailing address.
 * @param {DisplayField[]} displayFields an array of parcel display fields
 * @return {DisplayField[]} the displayFields in the new format
 */
function formatOldParcelDisplayFields(displayFields) {
  displayFields.forEach((displayField) => {
    if (displayField.label === 'Address') {
      // re-aggregate the address display fields
      const originalDfs = displayField.displayFields;
      const streetAddress = {
        unit: 'string',
        allowEdit: false,
        allowDelete: true,
        label: 'Address',
        value: `${originalDfs[0].value}, ${originalDfs[1].value}, ${originalDfs[2].value}`,
      };
      const city = {
        unit: 'string',
        allowEdit: false,
        allowDelete: true,
        label: 'City',
        value: originalDfs[3].value,
      };
      const state = {
        unit: 'string',
        allowEdit: false,
        allowDelete: true,
        label: 'State',
        value: originalDfs[4].value,
      };
      const zip = {
        unit: 'string',
        allowEdit: false,
        allowDelete: true,
        label: 'Zip Code',
        value: originalDfs[5].value,
      };
      displayField.displayFields = [streetAddress, city, state, zip];
    } else if (displayField.label === 'Mailing Address') {
      // re-aggregate the mailing address display fields
      const first = displayField.displayFields[0].value;
      const second = displayField.displayFields[1].value;
      displayField.value = `${first}, ${second}`;
      displayField.unit = 'string';
      delete displayField.displayFields;
    }
  });
  return displayFields;
}

/**
 * Formats the display fields for Itamlink tax parcels when they are initially imported
 * @param {object} itamLinkFields the display fields for the itamlink tax parcel
 * @return {DisplayField[]} the display fields in the new format
 */
function formatItamLinkFields(itamLinkFields) {
  // Apply the standard fields to the display fields
  const displayFields = [];
  mappedFields?.['itamlinkTaxParcel']?.forEach((field) => {
    const itamLinkField = itamLinkFields.find((df) => df.label === field.label);
    if (!itamLinkField || !itamLinkField.label) return;

    // These display fields will be nested under the property address
    if (itamLinkField.label === 'City' || itamLinkField.label === 'State') return;

    const displayField = new DisplayField({
      label: itamLinkField.label,
      unit: field.formatUnit ?? field.unit,
      allowEdit: true,
      allowLabelEdit: false,
      allowDelete: true,
      value: itamLinkField.value,
    });

    // Format the property address display fields
    if (itamLinkField.label === 'Property Address') {
      const addressDisplayFields = ['Property Address', 'City', 'State']
        .map((key) => { // map to display fields
          const field = itamLinkFields?.find((field) => field.label === key);
          return new DisplayField({
            label: key,
            unit: 'string',
            value: field?.value || undefined,
            allowEdit: true,
            allowLabelEdit: false,
            allowDelete: true,
          });
        });
      displayField.value = null;
      displayField.displayFields = addressDisplayFields;
      displayField.unit = 'address';
    }

    displayFields.push(displayField);
  });
  return displayFields;
}

const LINKED_LABEL_ACTION = 'linkedLabel';

/**
 * Overlay and shared map features other than labels.
 * @param {Feature} feature
 * @return {boolean}
 */
function canAddLinkedLabel(feature) {
  return feature?.featureType !== 'label' && typeof feature?.parent?.newFeature === 'function';
}

/**
 * Null once the source is no longer in the label's overlay or shared map.
 * @param {Feature} feature
 * @return {Feature|null}
 */
function getLinkedLabelSource(feature) {
  const sourceId = feature?.linkedLabel?.featureId;
  if (!sourceId) return null;
  return feature.parent?.features?.get(sourceId) ?? null;
}

/**
 * createFeatureContent - given a feature, creates infoWindowContent
 * @param {Feature} feature - the feature to make infoWindowContent for
 * @param {Toolbar} toolbar - the toolbar to use for the infoWindowContent
 * @param {google.maps.LatLng|google.maps.LatLngLiteral|{latitude:number,longitude:number}} location - the location of
 * the feature
 * @param {boolean} editing - whether the feature should be created as actively editing
 * @return {InfoWindowContent} - the infoWindowContent for a feature
 */
function createFeatureContent(feature, toolbar, location, editing = false) {
  try {
    const oldParcelDisplayFields = feature.displayFields?.some((displayField) => {
      if (displayField.label === 'Mailing Address' && displayField.displayFields) {
        return true;
      }
      return false;
    });

    if (oldParcelDisplayFields) {
      feature.displayFields = formatOldParcelDisplayFields(feature.displayFields);
    }

    const nonEditableFields = [
      'Drive Time',
      'Distance',
      'Acreage Calculated',
      'Calculated Distance',
      'Calculated SF',
      ...standardFields['itamlinkTaxParcel'],
    ];

    // When opening any feature, override aggregated fields
    feature.displayFields?.forEach((displayField, idx) => {
      displayField.allowDelete = true;
      if (!nonEditableFields.includes(displayField.label)) {
        displayField.allowEdit = true;
        displayField.allowLabelEdit = true;
      }
      // If the display field is a hyperlink, update it
      if (isHyperLink(displayField.value) && displayField.unit === 'link') {
        displayField.value = new Link({url: displayField.value, text: 'Click Here', type: 'link'});
      }
    });

    const comps = [];
    if (feature.comps != undefined) {
      for (const comp of feature.comps) {
        for (const displayField of comp.displayFields) {
          // if the name has a period in it, it gets registered and converted to a link object. This makes it not a link
          if (displayField.label === 'Name' && displayField.unit === 'link') {
            displayField.unit = 'string';
            displayField.value = displayField.value.url;
          }
          if (isHyperLink(displayField.value) && displayField.unit === 'link') {
            displayField.value = new Link({url: displayField.value, text: 'Click Here', type: 'link'});
          }
          displayField.allowEdit = true;
          displayField.allowLabelEdit = true;
          displayField.allowDelete = true;
        }
        comps.push(comp);
      }
    }

    // Don't display the feature name as the title if it is a default name
    const defaultFeatureNames = Array
      .from(featureOptions.values())
      .map((option) => option.defaultName);
    // Create content object for making InfoWindowContent instance
    const allContent = new InfoWindowContent({
      type: 'feature',
      title: defaultFeatureNames.includes(feature.name) ? null : feature.name,
      id: feature.id,
      metadata: {},
      displayFields: feature.displayFields ?? [],
      toolbar: toolbar ?? {toolbarOptions: []},
      location: location,
      featureType: feature.featureType,
      tabIcon: feature.icon,
      host: {},
      editing,
      comps,
    });

    watchEffect(() => allContent.title = defaultFeatureNames.includes(feature.name) ? null : feature.name);

    if (feature.graphic) {
      allContent.host.setHighlight = async (state) => (await feature.graphic)?.setHighlight(state);
      allContent.host.isTaxFeature = feature.isTaxFeature;
      // If private is used by non Toolbox overlays, we'll need to update this
      allContent.host.isToolboxFeature = feature.parent?.name === 'Toolbox Overlay' && feature.parent.private;
    }
    if (feature.setEditing) {
      allContent.host.setEditing = async (state) => {
        if (feature.parent?.editMode === false) return;
        // If the feature is a tax feature and the user is not on the tax team, don't allow editing
        if (feature.isTaxFeature && !getStore().state.taxTeamIds?.includes(currentUser()?.uid)) return;
        return await feature?.setEditing(state);
      };
    }
    if (allContent.featureType === 'placeMarker') {
      allContent.host.closeActions = () => {
        if (Markers.exists(`${allContent.type}s`, allContent.id)) {
          Markers.setDraggable(`${allContent.type}s`, allContent.id, false);
        }
      };
    }


    const switchMode = (editing, toolbarOption) => {
      allContent.editing = editing;
      const featureToolbar = allContent.toolbar;

      // Placemarker features are draggable when in edit mode
      if (allContent.featureType === 'placeMarker' && Markers.exists(`features`, allContent.id)) {
        Markers.setDraggable(`features`, allContent.id, editing);
      }

      if (featureToolbar.toolbarOptions.length === 0) {
        featureToolbar.toolbarOptions.push([toolbarOption]);
        return;
      }
      const deleteSection = featureToolbar.toolbarOptions.find((section) => section.some((option) => option.action === 'delete'));
      if (deleteSection) {
        const modeButtonIndex = deleteSection.findIndex((option) => option.action === 'enterEditMode' || option.action === 'enterViewMode');
        if (modeButtonIndex !== -1) {
          deleteSection[modeButtonIndex] = toolbarOption;
        } else {
          deleteSection.push(toolbarOption);
        }
      } else {
        featureToolbar.toolbarOptions.push([toolbarOption]);
      }
    };
    const enterViewMode = () => {
      switchMode(false, {
        icon: 'Pencil',
        name: 'Edit Feature',
        action: 'enterEditMode',
        callback: () => enterEditMode(),
      });
    };
    const enterEditMode = () => {
      switchMode(true, {
        icon: 'Check',
        name: 'Finish Editing',
        action: 'enterViewMode',
        callback: () => enterViewMode(),
      });
    };

    let deleting = false; // Fast people can click the delete button twice, so short circuit deleting twice.
    const deleteButton = {
      action: 'delete',
      icon: 'Trash',
      name: 'deleteFeature',
      callback: async () => {
        if (deleting) return;
        deleting = true;
        const restoreFn = await feature.parent.deleteFeature(feature.id);
        await feature.setRender?.(false);
        if (restoreFn) {
          getStore().commit('setSnack', {
            message: 'Feature has been deleted',
            action: {text: 'Undo', callback: restoreFn},
          });
        }
        deleting = false;
      },
    };

    // new labels shouldn't have a delete button because they haven't been fully added to the overlay
    if (feature?.id !== NEW_LABEL_ID) {
      allContent.toolbar.toolbarOptions.push([deleteButton]);
    }

    const linkedLabelSource = getLinkedLabelSource(feature);
    if (linkedLabelSource) {
      allContent.toolbar.toolbarOptions.at(-1).push({
        icon: 'Pencil',
        action: 'editLinkedLabel',
        name: 'Edit linked label fields',
        callback: () => {
          getStore().commit('setLinkedLabelToEdit', feature);
          getStore().commit('setLinkedLabelFeature', linkedLabelSource);
        },
      });
    }

    if (allContent.featureType === 'placeMarker' && allContent.editing) {
      enterEditMode();
    } else if (allContent.featureType !== 'label') {
      enterViewMode();
    }

    // If the feature is a tax parcel and the user is on the tax team, allow importing itamlink fields
    if (feature.isTaxFeature && feature.featureType === 'polygon' && getStore().state.taxTeamIds.includes(currentUser().uid)) {
      // Add the import itamlink fields button to the toolbar
      allContent.toolbar.toolbarOptions[0].unshift({
        icon: 'AddLink',
        action: 'itamlink',
        name: 'Import itamlink fields',
        callback: () => getStore().commit('setItamlinkFeature', feature),
      });

      // Allow setting the display fields from the info window
      allContent.host.setDisplayFields = async (fields) => feature?.update({displayFields: fields});

      // Apply the standard fields to the display fields
      mappedFields?.['itamlinkTaxParcel']?.forEach((field) => {
        const displayField = allContent.displayFields.find((df) => df.label === field.label);
        if (!displayField || String(displayField).trim() === '') return;

        Object.assign(displayField, new DisplayField({
          label: field.label,
          unit: field.formatUnit ?? field.unit,
          allowEdit: true,
          allowLabelEdit: false,
          allowDelete: true,
          value: displayField.value,
        }));
      });
    }

    const isTaxFeature = feature.isTaxFeature;
    const isTaxTeamMember = getStore().state.taxTeamIds?.includes(currentUser().uid);
    const isEditableMap = getStore().state.isEditableMap;
    if ((isTaxFeature && !isTaxTeamMember) || !isEditableMap) {
      // If the feature should not be edited by the current user, wipe the toolbar
      allContent.toolbar.toolbarOptions = [];
    }

    if (getStore().state.allowFeatureGrid) {
      const featureGridButton = {
        icon: 'Table',
        action: 'featureGrid',
        name: 'Feature Grid',
        callback: () => getStore().commit('setFeatureGrid', {
          features: feature.parent.features,
          featureType: feature.featureType,
        }),
      };

      // watch the value of hasDisplayFields to make the Feature Grid button reactive
      watchEffect(() => {
        const hasDisplayFields = feature.displayFields?.length > 0 ||
          feature.comps?.some((comp) => comp.displayFields?.length > 0);

        const featureGridSection = allContent.toolbar.toolbarOptions[0];
        const hasFeatureGridButton = featureGridSection?.some((option) => option.action === 'featureGrid');

        if (hasDisplayFields && !hasFeatureGridButton) {
          if (featureGridSection) {
            featureGridSection.unshift(featureGridButton);
          } else {
            allContent.toolbar.toolbarOptions.unshift([featureGridButton]);
          }
        } else if (!hasDisplayFields && hasFeatureGridButton) {
          if (featureGridSection) {
            const buttonIndex = featureGridSection.findIndex((option) => option.action === 'featureGrid');
            if (buttonIndex !== -1) {
              featureGridSection.splice(buttonIndex, 1);
              if (featureGridSection.length === 0) {
                allContent.toolbar.toolbarOptions.shift();
              }
            }
          }
        }
      });
    }

    const canEditFeature = !((isTaxFeature && !isTaxTeamMember) || !isEditableMap);
    if (canAddLinkedLabel(feature) && canEditFeature) {
      const linkedLabelButton = {
        icon: 'Label',
        action: LINKED_LABEL_ACTION,
        name: 'Create linked label',
        callback: () => getStore().commit('setLinkedLabelFeature', feature),
      };

      // A linked label needs at least one field to show
      watchEffect(() => {
        const hasDisplayFields = feature.displayFields?.length > 0;
        const firstSection = allContent.toolbar.toolbarOptions[0];
        const buttonIndex = firstSection?.findIndex((option) => option.action === LINKED_LABEL_ACTION) ?? -1;

        if (hasDisplayFields && buttonIndex === -1) {
          if (firstSection) {
            const insertIndex = firstSection[0]?.action === 'featureGrid' ? 1 : 0;
            firstSection.splice(insertIndex, 0, linkedLabelButton);
          } else {
            allContent.toolbar.toolbarOptions.unshift([linkedLabelButton]);
          }
        } else if (!hasDisplayFields && buttonIndex !== -1) {
          firstSection.splice(buttonIndex, 1);
          if (firstSection.length === 0) {
            allContent.toolbar.toolbarOptions.shift();
          }
        }
      });
    }

    return allContent;
  } catch (error) {
    console.warn('Error creating feature content', error);
    return null;
  }
}

/**
 * @this {InfoWindowContent}
 */
async function saveFeature() {
  if (!this) {
    throw new Error('saveFeature() must only be called after binding it to an InfoWindowContent instance');
  }

  // Get the feature by id from the features collection
  const featuresCollection = this.host?.isTaxFeature ? getCollection('taxFeatures') : getCollection('features');
  const existingFeature = featuresCollection.getItem(this.id);

  let comps = [];
  if (this.comps) {
    comps = this.comps.map((comp) => {
      return {
        type: comp.type,
        id: comp.id ?? Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
        displayFields: comp.displayFields,
        name: comp.name ?? '',
      };
    });
  }

  // Update that feature with the new title and display fields
  await existingFeature.update({
    name: this.title ?? null,
    displayFields: this.displayFields,
    comps,
  });
}

export {
  formatItamLinkFields,
  createFeatureContent,
  saveFeature,
};
